import { useCallback, useEffect, useState } from 'react';

import { SiteSchedule } from '../models/SiteSchedule';
import { SiteVisitLog } from '../models/SiteVisitLog';
import { SiteIncident } from '../models/SiteIncident';
import { SiteScheduleService } from '../services/SiteScheduleService';
import { SiteVisitLogService } from '../services/SiteVisitLogService';
import { SiteIncidentService } from '../services/SiteIncidentService';

export interface SiteTimelineData {
  schedules: SiteSchedule[];
  visits: SiteVisitLog[];
  incidents: SiteIncident[];
  loading: boolean;
  error: string | null;
  /** 3종 데이터를 다시 전부 조회. mutation(생성/수정/삭제) 후 호출. */
  reload: () => Promise<void>;
}

/**
 * 현장 1개의 일정 / 출장 일지 / 장애 이력을 한 번에 조회하는 공용 훅.
 *
 * SiteSchedulesPage(캘린더)와 UnifiedScheduleSection(통합 타임라인)이 동일하게
 * `Promise.all([Schedule, VisitLog, Incident].list)` 를 반복하던 것을 통합한다.
 * 현장 1개의 데이터량이라 전체를 한 번 가져와 클라이언트에서 가공한다.
 *
 * @param projectId  현장 프로젝트 id. 유한수가 아니면(NaN 등) 조회를 건너뛴다.
 * @param errorMessage  Error 인스턴스가 아닐 때 표시할 fallback 메시지.
 */
export function useSiteTimelineData(
  projectId: number,
  errorMessage: string = '데이터 로드 실패',
): SiteTimelineData {
  const [schedules, setSchedules] = useState<SiteSchedule[]>([]);
  const [visits, setVisits] = useState<SiteVisitLog[]>([]);
  const [incidents, setIncidents] = useState<SiteIncident[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!Number.isFinite(projectId)) return;
    setLoading(true);
    setError(null);
    try {
      const [s, v, i] = await Promise.all([
        SiteScheduleService.list(projectId),
        SiteVisitLogService.list(projectId),
        SiteIncidentService.list(projectId),
      ]);
      setSchedules(s);
      setVisits(v);
      setIncidents(i);
    } catch (e) {
      setError(e instanceof Error ? e.message : errorMessage);
    } finally {
      setLoading(false);
    }
  }, [projectId, errorMessage]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { schedules, visits, incidents, loading, error, reload };
}
